import { useRef, useState } from 'react';

import { EyeOutlined } from '@ant-design/icons';
import { Button, Card, Table, Tag, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';

import {
  TiendaPerfectaReportRoute,
  TiendaPerfectaRouteStop,
} from 'models/TiendaPerfecta';

import { useFlipReorder } from '../hooks/useFlipReorder';
import {
  ROUTE_STATE_TAG_COLOR,
  STOP_STATE_TAG_COLOR,
  getRouteUiState,
  getStopUiState,
} from '../types';
import { formatTime } from '../utils/time';
import AnimatedNumber from './AnimatedNumber';
import StopDetailModal from './StopDetailModal';
import { StopDetailHint } from './StopDetailSkeleton';

const I18N_BASE = 'scenes.route_report.components.routes_table';

const DASH = <span className="route-report-muted">—</span>;

interface StopsTableProps {
  stops: TiendaPerfectaRouteStop[];
  userTimeZone: string;
  onOpenStop: (stop: TiendaPerfectaRouteStop) => void;
}

/**
 * Las paradas de una ruta.
 *
 * Usa el mismo lenguaje de encabezado que la tabla que la contiene: lo que
 * indica el anidamiento es la superficie —una card blanca sobre el gris de la
 * fila expandida—, no un segundo estilo tipográfico.
 *
 * El "Orden" sale de `sequence`, que es lo que publica el contrato y lo mismo
 * que numera el chip del detalle: así la fila y su modal no pueden discrepar.
 */
function StopsTable({ stops, userTimeZone, onOpenStop }: StopsTableProps) {
  const { t } = useTranslation();

  const columns: ColumnsType<TiendaPerfectaRouteStop> = [
    {
      title: t(`${I18N_BASE}.stops.order`),
      key: 'order',
      width: 80,
      /* El mismo chip que encabeza el modal de la parada, para que la fila y su
         detalle se reconozcan como lo mismo.

         Numera por `sequence`, el campo del contrato, y no por la posición en
         la lista: así los dos números no pueden discrepar si el servidor
         cambiara el orden de `stops[]`. */
      render: (_value, stop) => (
        <span className="route-report-seq">{stop.sequence}</span>
      ),
    },
    {
      title: t(`${I18N_BASE}.stops.place`),
      key: 'place',
      render: (_value, stop) => (
        <>
          {/* `label` puede venir null: se cae a `code`, que nunca falta. */}
          <span className="route-report-stop__name">
            {stop.label || stop.code}
          </span>
          <br />
          <span className="route-report-stop__code">{stop.code}</span>
        </>
      ),
    },
    {
      title: t(`${I18N_BASE}.stops.time`),
      key: 'time',
      width: 110,
      render: (_value, stop) => {
        // El instante que representa a la parada depende de su estado: la hora
        // de cierre si terminó, la de inicio si está en curso.
        const iso =
          stop.status === 'finished' ? stop.finishedAt : stop.startedAt;
        const time = formatTime(iso, userTimeZone);

        return time ? <span className="route-report-num">{time}</span> : DASH;
      },
    },
    {
      title: t(`${I18N_BASE}.stops.state`),
      key: 'state',
      width: 160,
      render: (_value, stop) => {
        const state = getStopUiState(stop.status, Boolean(stop.bypass));

        return (
          <Tag color={STOP_STATE_TAG_COLOR[state]}>
            {t(`scenes.route_report.stop_states.${state}`)}
          </Tag>
        );
      },
    },
    {
      title: t(`${I18N_BASE}.stops.reason`),
      key: 'reason',
      width: 230,
      render: (_value, stop) =>
        stop.bypass ? (
          <>
            <span className="route-report-bypass">
              {stop.bypass.reason}{' '}
              <span className="route-report-stop__code">
                {stop.bypass.code}
              </span>
            </span>
            {/* El comentario de terreno va debajo y atenuado: es contexto de la
                incidencia, no la incidencia. */}
            {stop.bypass.note && (
              <>
                <br />
                <span className="route-report-bypass__note">
                  {stop.bypass.note}
                </span>
              </>
            )}
          </>
        ) : (
          DASH
        ),
    },
    {
      title: t(`${I18N_BASE}.stops.detail`),
      key: 'detail',
      width: 90,
      align: 'center',
      render: (_value, stop) =>
        /* Una parada con `id` en `null` es el marcador de posición de una ruta
           que todavía no se ejecutó: no hay detalle al cual enlazar. El botón
           se deshabilita en vez de desaparecer para que la columna no se
           descuadre entre una fila y otra. */
        stop.id ? (
          <Tooltip title={t(`${I18N_BASE}.stops.view_detail`)}>
            <Button
              size="small"
              icon={<EyeOutlined />}
              aria-label={t(`${I18N_BASE}.stops.view_detail`)}
              onClick={(event) => {
                // La fila entera abre y cierra la ruta: sin esto, mirar una
                // parada colapsaría el bloque que la contiene.
                event.stopPropagation();
                onOpenStop(stop);
              }}
            />
          </Tooltip>
        ) : (
          /* El `<span>` no es decorativo: un `<button disabled>` no despacha
             eventos de mouse, así que un Tooltip puesto directo sobre él nunca
             abre y la explicación queda sólo en el `aria-label`. El envoltorio
             es quien recibe el hover. */
          <Tooltip title={t(`${I18N_BASE}.stops.no_detail`)}>
            <span className="route-report-stop__no-detail">
              <Button
                size="small"
                disabled
                icon={<EyeOutlined />}
                aria-label={t(`${I18N_BASE}.stops.no_detail`)}
              />
            </span>
          </Tooltip>
        ),
    },
  ];

  return (
    <div className="route-report-subtable">
      <Table
        columns={columns}
        dataSource={stops}
        rowKey={(stop) => `${stop.sequence}-${stop.code}`}
        pagination={false}
        size="middle"
      />
    </div>
  );
}

interface RoutesTableProps {
  routes: TiendaPerfectaReportRoute[];
  excludedRoutes: number;
  userTimeZone: string;
  loading?: boolean;
}

/**
 * Las rutas del día.
 *
 * `routes[]` ya viene en orden de grilla desde el servidor —no iniciadas, en
 * ruta, terminadas— así que acá no se ordena nada: ninguna columna es sortable
 * a propósito, para no romper ese criterio sin querer.
 */
function RoutesTable({
  routes,
  excludedRoutes,
  userTimeZone,
  loading = false,
}: RoutesTableProps) {
  const { t } = useTranslation();

  // La expansión se controla desde acá para poder abrirla desde la fila entera y
  // no solo desde el botón, y para marcar la fila abierta.
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);

  /**
   * La parada que se está mirando, con la ruta a la que pertenece.
   *
   * Se guarda todo el contexto y no sólo el `id` porque el modal lo necesita
   * ANTES de que su petición vuelva: con esto pinta el encabezado completo de
   * entrada y reserva el espacio correcto para los dos bloques que dependen del
   * estado —la alerta del motivo y el mapa—, así nada crece ni se mueve cuando
   * llega la respuesta.
   */
  const [openStop, setOpenStop] = useState<{
    stop: TiendaPerfectaRouteStop;
    route: TiendaPerfectaReportRoute;
  } | null>(null);

  /**
   * Si el modal está abierto se guarda APARTE de qué parada muestra.
   *
   * Al cerrar sólo se baja esta bandera y la parada queda: antd desmonta el
   * contenido recién cuando termina la animación de salida, así que borrar la
   * parada acá le sacaría al modal la identidad que está mostrando y el
   * encabezado se derrumbaría a la vista durante todo el fundido.
   */
  const [isStopOpen, setIsStopOpen] = useState(false);

  const openStopDetail = (
    stop: TiendaPerfectaRouteStop,
    route: TiendaPerfectaReportRoute
  ) => {
    setOpenStop({ stop, route });
    setIsStopOpen(true);
  };

  const stopHint: StopDetailHint | undefined = openStop
    ? {
        sequence: openStop.stop.sequence,
        code: openStop.stop.code,
        label: openStop.stop.label,
        status: openStop.stop.status,
        hasBypass: Boolean(openStop.stop.bypass),
        hasBypassNote: Boolean(openStop.stop.bypass?.note),
        routeCode: openStop.route.code,
        /* El distribuidor del reporte es el VIGENTE de la ruta y el del detalle
           es el congelado al check-in: pueden discrepar en una ruta reasignada.
           El encabezado se corrige solo cuando llega la respuesta, porque la
           fuente pasa a ser el detalle. */
        distributorName: openStop.route.distributor?.tradeName ?? null,
      }
    : undefined;

  /**
   * Acá una fila NO se mueve al avanzar: el servidor ordena por ESTADO, así que
   * solo salta cuando una ruta arranca o termina, y entonces cruza un grupo
   * entero. Es un movimiento largo y conviene acompañarlo.
   */
  const tableRef = useRef<HTMLDivElement>(null);
  useFlipReorder(tableRef);

  const toggleRow = (routeId: string) =>
    setExpandedKeys((keys) =>
      keys.includes(routeId)
        ? keys.filter((key) => key !== routeId)
        : [...keys, routeId]
    );

  const columns: ColumnsType<TiendaPerfectaReportRoute> = [
    {
      title: t(`${I18N_BASE}.columns.route`),
      dataIndex: 'code',
      key: 'code',
      render: (code: string) => (
        <span className="route-report-code">{code}</span>
      ),
    },
    {
      title: t(`${I18N_BASE}.columns.distributor`),
      key: 'distributor',
      ellipsis: true,
      render: (_value, route) => route.distributor?.tradeName ?? DASH,
    },
    {
      title: t(`${I18N_BASE}.columns.unit`),
      key: 'unit',
      render: (_value, route) =>
        route.van ? (
          /* La unidad se rotula con su nombre operativo; la patente queda en el
             `title` para quien necesite el dato duro. */
          <span
            className="route-report-van"
            title={t('scenes.route_report.van_plate', {
              plate: route.van.plate,
            })}
          >
            {route.van.name || route.van.plate}
          </span>
        ) : (
          DASH
        ),
    },
    {
      title: t(`${I18N_BASE}.columns.state`),
      key: 'state',
      render: (_value, route) => {
        const state = getRouteUiState(route.status);

        return (
          <Tag color={ROUTE_STATE_TAG_COLOR[state]}>
            {t(`scenes.route_report.route_states.${state}`)}
          </Tag>
        );
      },
    },
    {
      title: t(`${I18N_BASE}.columns.start`),
      key: 'start',
      render: (_value, route) => {
        // Una ruta `not_started` puede tener `startedAt`: es la ventana entre el
        // check-in y la primera parada. Ahí no se muestra hora de inicio, porque
        // la ruta todavía no arrancó según el criterio del reporte.
        if (route.status === 'not_started') return DASH;
        const time = formatTime(route.startedAt, userTimeZone);

        return time ? <span className="route-report-num">{time}</span> : DASH;
      },
    },
    {
      title: t(`${I18N_BASE}.columns.stops`),
      key: 'stops',
      render: (_value, route) => (
        <span className="route-report-num">
          <AnimatedNumber value={route.finishedStopCount} /> / {route.stopCount}
        </span>
      ),
    },
    {
      title: t(`${I18N_BASE}.columns.progress`),
      key: 'progress',
      width: 160,
      render: (_value, route) => {
        const percentage = route.stopCount
          ? Math.round((route.finishedStopCount / route.stopCount) * 100)
          : 0;

        return (
          <div className="route-report-mini">
            <div className="route-report-mini__track">
              <div
                className="route-report-mini__fill"
                style={{ width: `${percentage}%` }}
              />
            </div>
            <AnimatedNumber
              className="route-report-mini__value"
              value={percentage}
              format={(n) => `${n}%`}
            />
          </div>
        );
      },
    },
  ];

  const done = routes.reduce((acc, r) => acc + r.finishedStopCount, 0);
  const total = routes.reduce((acc, r) => acc + r.stopCount, 0);

  return (
    <Card
      className="route-report-routes"
      title={
        <div>
          <span className="route-timeline__title">
            {t(`${I18N_BASE}.title`)}
          </span>
          <span className="route-timeline__subtitle">
            {t(`${I18N_BASE}.subtitle`)}
          </span>
        </div>
      }
    >
      <div ref={tableRef}>
        <Table
          columns={columns}
          dataSource={routes}
          rowKey="routeId"
          loading={loading}
          pagination={false}
          scroll={{ x: 'max-content' }}
          expandable={{
            expandedRowKeys: expandedKeys,
            onExpand: (_expanded, route) => toggleRow(route.routeId),
            expandedRowRender: (route) => (
              <StopsTable
                stops={route.stops}
                userTimeZone={userTimeZone}
                onOpenStop={(stop) => openStopDetail(stop, route)}
              />
            ),
            rowExpandable: (route) => route.stops.length > 0,
          }}
          onRow={(route) => ({
            // El hook de reordenamiento mide por este atributo: `rowKey` no llega
            // al DOM.
            'data-flip-key': route.routeId,
            // Toda la fila abre y cierra el detalle. El botón sigue existiendo
            // para que la acción sea alcanzable por teclado.
            onClick: () => route.stops.length > 0 && toggleRow(route.routeId),
          })}
          rowClassName={(route) =>
            [
              route.stops.length > 0 ? 'route-report-row--clickable' : '',
              expandedKeys.includes(route.routeId)
                ? 'route-report-row--expanded'
                : '',
            ]
              .filter(Boolean)
              .join(' ')
          }
        />
      </div>

      <div className="route-report-routes__foot">
        <span>
          {/* `count` es la variable que i18next usa para elegir el plural: sin
              ella no resuelve `_one`/`_other` y se imprime la key cruda. */}
          {t(`${I18N_BASE}.summary`, {
            count: routes.length,
            done,
            total,
          })}
        </span>
        {/* Es una afirmación sobre la completitud de la lista: sin esto la
            grilla parece completa cuando no lo está. */}
        {excludedRoutes > 0 && (
          <span>{t(`${I18N_BASE}.excluded`, { count: excludedRoutes })}</span>
        )}
      </div>

      <StopDetailModal
        stopId={openStop?.stop.id ?? undefined}
        isOpen={isStopOpen}
        onClose={() => setIsStopOpen(false)}
        userTimeZone={userTimeZone}
        hint={stopHint}
      />
    </Card>
  );
}

export default RoutesTable;
